import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Chip,
  LinearProgress,
  Fade,
  Grow,
  Card,
  CardContent,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// Icons
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import DataObjectIcon from '@mui/icons-material/DataObject';
import TransformIcon from '@mui/icons-material/Transform';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FunctionsIcon from '@mui/icons-material/Functions';
import MergeIcon from '@mui/icons-material/Merge';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';

const dataSources = [
  { id: 'postgres', name: 'PostgreSQL', icon: '🐘', color: '#336791', description: 'Relational Database' },
  { id: 'mongodb', name: 'MongoDB', icon: '🍃', color: '#47A248', description: 'Document Database' },
  { id: 's3', name: 'AWS S3', icon: '☁️', color: '#FF9900', description: 'Cloud Storage' },
  { id: 'mysql', name: 'MySQL', icon: '🐬', color: '#4479A1', description: 'SQL Database' },
];

const transformationBlocks = [
  { id: 'filter', label: 'Filter Rows', icon: FilterAltIcon, color: '#3B82F6' },
  { id: 'transform', label: 'Transform', icon: TransformIcon, color: '#8B5CF6' },
  { id: 'aggregate', label: 'Aggregate', icon: FunctionsIcon, color: '#EC4899' },
  { id: 'join', label: 'Join Data', icon: MergeIcon, color: '#10B981' },
  { id: 'clean', label: 'Clean Data', icon: CleaningServicesIcon, color: '#F59E0B' },
];

const InteractiveETLDemo = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedSource, setSelectedSource] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionComplete, setExecutionComplete] = useState(false);
  const [nextNodeId, setNextNodeId] = useState(1);

  const steps = ['Select Source', 'Build Pipeline', 'Execute', 'Monitor'];

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, style: { stroke: '#3B82F6' } }, eds)),
    [setEdges]
  );

  useEffect(() => {
    if (selectedSource && nodes.length === 0) {
      const sourceNode = {
        id: 'source',
        type: 'input',
        data: { 
          label: (
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography sx={{ fontSize: '1.5rem', mb: 0.5 }}>{selectedSource.icon}</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff' }}>
                {selectedSource.name}
              </Typography>
            </Box>
          ) 
        },
        position: { x: 50, y: 150 },
        style: {
          background: selectedSource.color,
          color: '#fff',
          border: '2px solid rgba(255,255,255,0.3)',
          borderRadius: '12px',
          padding: '8px',
          minWidth: '120px',
        },
      };
      setNodes([sourceNode]);
    }
  }, [selectedSource, nodes.length, setNodes]);

  const handleSourceSelect = (source) => {
    setSelectedSource(source);
    setActiveStep(1);
    setNodes([]);
    setEdges([]);
    setNextNodeId(1);
  };

  const handleAddTransformation = (block) => {
    const newNode = {
      id: `transform-${nextNodeId}`,
      data: {
        label: (
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <block.icon sx={{ fontSize: '1.25rem', mb: 0.5, color: '#fff' }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#fff' }}>
              {block.label}
            </Typography>
          </Box>
        ),
      },
      position: { x: 50 + nextNodeId * 200, y: 150 },
      style: {
        background: block.color,
        color: '#fff',
        border: '2px solid rgba(255,255,255,0.3)',
        borderRadius: '12px',
        padding: '8px',
        minWidth: '120px',
      },
    };

    setNodes((nds) => [...nds, newNode]);
    setNextNodeId((prev) => prev + 1);

    if (nodes.length > 0) {
      const lastNode = nodes[nodes.length - 1];
      setEdges((eds) => [
        ...eds,
        {
          id: `e-${lastNode.id}-${newNode.id}`,
          source: lastNode.id,
          target: newNode.id,
          animated: true,
          style: { stroke: '#3B82F6', strokeWidth: 2 },
        },
      ]);
    }
  };

  const handleExecutePipeline = () => {
    setActiveStep(2);
    setExecutionProgress(0);
    setExecutionLogs([]);
    setExecutionComplete(false);

    const logs = [
      { time: '00:00', message: 'Initializing pipeline...' },
      { time: '00:01', message: `Connected to ${selectedSource.name}` },
      { time: '00:02', message: 'Extracting data: 125,000 rows' },
      { time: '00:03', message: 'Applying transformations...' },
      { time: '00:05', message: 'Data validation passed' },
      { time: '00:06', message: 'Loading to destination...' },
      { time: '00:08', message: 'Pipeline executed successfully ✓' },
    ];

    let logIndex = 0;
    const logInterval = setInterval(() => {
      if (logIndex < logs.length) {
        setExecutionLogs((prev) => [...prev, logs[logIndex]]);
        setExecutionProgress(((logIndex + 1) / logs.length) * 100);
        logIndex++;
      } else {
        clearInterval(logInterval);
        setTimeout(() => {
          setExecutionComplete(true);
          setActiveStep(3);
        }, 500);
      }
    }, 1000);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedSource(null);
    setNodes([]);
    setEdges([]);
    setExecutionProgress(0);
    setExecutionLogs([]);
    setExecutionComplete(false);
    setNextNodeId(1);
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '700px',
        background: 'linear-gradient(135deg, rgba(15, 15, 25, 0.95) 0%, rgba(26, 15, 42, 0.95) 100%)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Header with Stepper */}
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(0, 0, 0, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography
            sx={{
              fontFamily: 'Sora, sans-serif',
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              color: '#fff',
            }}
          >
            Interactive ETL Demo
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            sx={{
              borderColor: 'rgba(147, 51, 234, 0.5)',
              color: '#fff',
              '&:hover': {
                borderColor: 'rgba(147, 51, 234, 0.8)',
                background: 'rgba(147, 51, 234, 0.1)',
              },
            }}
          >
            Reset
          </Button>
        </Box>

        <Stepper activeStep={activeStep} sx={{ maxWidth: '800px', mx: 'auto' }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&.Mui-active': { color: '#9333EA', fontWeight: 600 },
                    '&.Mui-completed': { color: '#10B981' },
                  },
                  '& .MuiStepIcon-root': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    '&.Mui-active': { color: '#9333EA' },
                    '&.Mui-completed': { color: '#10B981' },
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, minHeight: '500px' }}>
        <AnimatePresence mode="wait">
          {/* Step 1: Source Selection */}
          {activeStep === 0 && (
            <motion.div
              key="source-selection"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                sx={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  color: '#fff',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Select Your Data Source
              </Typography>
              <Grid container spacing={3} sx={{ maxWidth: '900px', mx: 'auto' }}>
                {dataSources.map((source) => (
                  <Grid item xs={12} sm={6} md={3} key={source.id}>
                    <Grow in timeout={500}>
                      <Card
                        onClick={() => handleSourceSelect(source)}
                        sx={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderColor: source.color,
                            boxShadow: `0 8px 24px ${source.color}40`,
                          },
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <Typography sx={{ fontSize: '3rem', mb: 1 }}>{source.icon}</Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '1rem',
                              color: '#fff',
                              mb: 0.5,
                            }}
                          >
                            {source.name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                            {source.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grow>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          )}

          {/* Step 2: Pipeline Builder */}
          {activeStep === 1 && (
            <motion.div
              key="pipeline-builder"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              style={{ height: '500px', display: 'flex', flexDirection: 'column' }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    color: '#fff',
                    mb: 2,
                  }}
                >
                  Add Transformation Blocks
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                  {transformationBlocks.map((block) => (
                    <Chip
                      key={block.id}
                      icon={<block.icon sx={{ color: '#fff !important' }} />}
                      label={block.label}
                      onClick={() => handleAddTransformation(block)}
                      sx={{
                        background: block.color,
                        color: '#fff',
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: '2px solid rgba(255,255,255,0.2)',
                        '&:hover': {
                          background: block.color,
                          opacity: 0.8,
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  overflow: 'hidden',
                }}
              >
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  fitView
                  style={{
                    background: 'transparent',
                  }}
                >
                  <Background color="rgba(255, 255, 255, 0.1)" gap={16} />
                  <Controls
                    style={{
                      background: 'rgba(0, 0, 0, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                    }}
                  />
                  <MiniMap
                    style={{
                      background: 'rgba(0, 0, 0, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                    }}
                    nodeColor={(node) => {
                      return node.style?.background || '#3B82F6';
                    }}
                  />
                </ReactFlow>
              </Box>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleExecutePipeline}
                  disabled={nodes.length < 2}
                  sx={{
                    background: 'linear-gradient(90deg, #9333EA 0%, #4F46E5 100%)',
                    color: '#fff',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1rem',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #7E22CE 0%, #4338CA 100%)',
                    },
                    '&:disabled': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  Execute Pipeline
                </Button>
                {nodes.length < 2 && (
                  <Typography sx={{ mt: 1, fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                    Add at least one transformation block to continue
                  </Typography>
                )}
              </Box>
            </motion.div>
          )}

          {/* Step 3: Execution */}
          {activeStep === 2 && (
            <motion.div
              key="execution"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                sx={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  color: '#fff',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Pipeline Execution
              </Typography>

              <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                <Paper
                  sx={{
                    p: 3,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>Progress</Typography>
                    <Typography sx={{ color: '#9333EA', fontWeight: 600 }}>
                      {Math.round(executionProgress)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={executionProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      background: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #9333EA 0%, #4F46E5 100%)',
                        borderRadius: 4,
                      },
                    }}
                  />
                </Paper>

                <Paper
                  sx={{
                    p: 3,
                    background: 'rgba(0, 0, 0, 0.5)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#10B981',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    &gt; Execution Logs
                  </Typography>
                  {executionLogs.map((log, index) => (
                    <Fade in key={index} timeout={300}>
                      <Typography
                        sx={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          mb: 1,
                        }}
                      >
                        <Box component="span" sx={{ color: '#9333EA', mr: 2 }}>
                          [{log.time}]
                        </Box>
                        {log.message}
                      </Typography>
                    </Fade>
                  ))}
                </Paper>
              </Box>
            </motion.div>
          )}

          {/* Step 4: Success Dashboard */}
          {activeStep === 3 && executionComplete && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                >
                  <CheckCircleIcon
                    sx={{
                      fontSize: '4rem',
                      color: '#10B981',
                      filter: 'drop-shadow(0 0 16px rgba(16, 185, 129, 0.6))',
                      mb: 2,
                    }}
                  />
                </motion.div>
                <Typography
                  sx={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    color: '#fff',
                    mb: 1,
                  }}
                >
                  Pipeline Executed Successfully!
                </Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Your data pipeline has been processed and monitored
                </Typography>
              </Box>

              <Grid container spacing={3} sx={{ maxWidth: '1000px', mx: 'auto' }}>
                {[
                  { label: 'Records Processed', value: '125,000', change: '+100%', icon: DataObjectIcon, color: '#3B82F6' },
                  { label: 'Throughput', value: '2.5K/sec', change: '+45%', icon: SpeedIcon, color: '#8B5CF6' },
                  { label: 'Success Rate', value: '99.9%', change: '+2.1%', icon: CheckCircleIcon, color: '#10B981' },
                  { label: 'Avg Latency', value: '120ms', change: '-18%', icon: TrendingUpIcon, color: '#F59E0B' },
                  { label: 'Data Quality', value: '98.5%', change: '+5.2%', icon: CleaningServicesIcon, color: '#EC4899' },
                  { label: 'Transformations', value: `${nodes.length - 1}`, change: '100%', icon: TransformIcon, color: '#06B6D4' },
                ].map((metric, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Grow in timeout={300 + index * 100}>
                      <Paper
                        sx={{
                          p: 3,
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: `1px solid ${metric.color}40`,
                          borderRadius: '12px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            background: 'rgba(255, 255, 255, 0.08)',
                            boxShadow: `0 8px 24px ${metric.color}40`,
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <metric.icon sx={{ color: metric.color, fontSize: '1.75rem' }} />
                          <Chip
                            label={metric.change}
                            size="small"
                            sx={{
                              background: `${metric.color}20`,
                              color: metric.color,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.875rem',
                            mb: 0.5,
                          }}
                        >
                          {metric.label}
                        </Typography>
                        <Typography
                          sx={{
                            color: '#fff',
                            fontSize: '1.75rem',
                            fontWeight: 700,
                          }}
                        >
                          {metric.value}
                        </Typography>
                      </Paper>
                    </Grow>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<RestartAltIcon />}
                  onClick={handleReset}
                  sx={{
                    borderColor: 'rgba(147, 51, 234, 0.5)',
                    color: '#fff',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'rgba(147, 51, 234, 0.8)',
                      background: 'rgba(147, 51, 234, 0.1)',
                    },
                  }}
                >
                  Try Another Pipeline
                </Button>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default InteractiveETLDemo;
